import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link as RouterLink, NavLink, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { StartupIcon, Icon } from '../../common/Icons';
import { useNavLinks } from '../Navbar/useNavLinks';
import { useAuth } from '../../../auth/AuthProvider';
import { useKnowledgeSearch } from '../../../api/hooks/knowledge.hook';
import type { SearchHit } from '../../../api/services/knowledge';
import { getDocumentContent } from '../../../api/services/knowledge';
import { useWorkspace } from '../../Workspace/artifacts';

const APP_TITLE = 'insora';

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  shortcut?: string;
  action: () => void;
  category: 'navigation' | 'action' | 'search';
}

interface SearchResultItem {
  id: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  snippet?: string;
  source?: string;
  type: 'command' | 'document';
  action: () => void;
}

type SourceDescriptor = {
  type: 'Document' | 'Connector' | 'Email';
  label: string;
  detail?: string;
};

const describeSearchSource = (source: string, artifacts?: Array<{ id: string; title: string }>): SourceDescriptor => {
  if (source.startsWith('connector:')) {
    const [, provider, ...rest] = source.split(':');
    const resource = rest.join(':');
    return {
      type: 'Connector',
      label: resource || provider,
      detail: provider.replace(/_/g, ' '),
    };
  }
  if (source.startsWith('emails:')) {
    const [, thread] = source.split(':', 2);
    return {
      type: 'Email',
      label: thread || 'Email thread',
      detail: 'Workspace emails',
    };
  }
  // Check if source is an artifact ID and resolve to title
  if (source.startsWith('artifact:') && artifacts) {
    const normalizeArtifactId = (raw: string) => raw.replace(/^artifact:/, '').replace(/:[0-9]+$/, '');
    const normalizedId = normalizeArtifactId(source);
    const artifact = artifacts.find((a) => {
      const normalizedArtifactId = normalizeArtifactId(a.id);
      return normalizedArtifactId === normalizedId || a.id === source;
    });
    if (artifact) {
      return {
        type: 'Document',
        label: artifact.title,
        detail: 'Workspace document',
      };
    }
  }
  return {
    type: 'Document',
    label: source,
    detail: 'Workspace document',
  };
};

const normalizeArtifactId = (raw: string) => raw.replace(/^artifact:/, '').replace(/:[0-9]+$/, '');

export const TopBar = () => {
  const navigate = useNavigate();
  const navLinks = useNavLinks();
  const { signOut, signingOut } = useAuth();
  const [isCommandOpen, setIsCommandOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [searchResults, setSearchResults] = useState<SearchHit[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [lastSearchQuery, setLastSearchQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  
  const searchMutation = useKnowledgeSearch();
  const { state: workspaceState, openArtifact, refreshArtifacts, createDocument } = useWorkspace();

  const handleSignOut = async () => {
    await signOut();
    // Redirect to landing page after signout
    window.location.href = 'https://insora.dev';
  };

  // Define available commands
  const commands: CommandItem[] = useMemo(
    () => [
      {
        id: 'nav-workspace',
        label: 'Go to Workspace',
        description: 'View your workspace dashboard',
        icon: <Icon name="Workspace" className="w-4 h-4" />,
        shortcut: 'G W',
        action: () => navigate('/'),
        category: 'navigation' as const,
      },
      {
        id: 'nav-knowledge',
        label: 'Go to Knowledge',
        description: 'Browse knowledge base',
        icon: <Icon name="Knowledge" className="w-4 h-4" />,
        shortcut: 'G K',
        action: () => navigate('/knowledge'),
        category: 'navigation' as const,
      },
      {
        id: 'nav-settings',
        label: 'Go to Settings',
        description: 'Configure workspace and view action logs',
        icon: <Icon name="Settings" className="w-4 h-4" />,
        shortcut: 'G S',
        action: () => navigate('/settings'),
        category: 'navigation' as const,
      },
      {
        id: 'nav-profile',
        label: 'Go to Profile',
        description: 'View your profile',
        icon: <Icon name="Profile" className="w-4 h-4" />,
        shortcut: 'G P',
        action: () => navigate('/profile'),
        category: 'navigation' as const,
      },
    ],
    [navigate],
  );

  // Clear search results when query changes (user is typing a new query)
  useEffect(() => {
    if (!query.trim()) {
      setSearchResults([]);
      setHasSearched(false);
      setLastSearchQuery('');
    } else if (hasSearched && query.trim() !== lastSearchQuery.trim()) {
      // If query changed after a search, reset search state to allow new search
      setHasSearched(false);
      setSearchResults([]);
    }
  }, [query, hasSearched, lastSearchQuery]);

  // Perform document search
  const handleSearch = useCallback(async () => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      setSearchResults([]);
      setHasSearched(false);
      setLastSearchQuery('');
      return;
    }

    setIsSearching(true);
    try {
      const response = await searchMutation.mutateAsync(trimmedQuery);
      // Ensure we have an array of hits
      const hits = response?.hits || [];
      setSearchResults(hits);
      setHasSearched(true);
      setLastSearchQuery(trimmedQuery);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
      setHasSearched(true);
      setLastSearchQuery(trimmedQuery);
      // Don't show error toast for search failures in command palette
    } finally {
      setIsSearching(false);
    }
  }, [query, searchMutation]);

  // Handle document click - same logic as KnowledgePage
  const handleOpenDocument = useCallback(
    async (hit: SearchHit) => {
      const descriptor = describeSearchSource(hit.source, workspaceState.artifacts);

      // Check if hit.source is an artifact ID (starts with "artifact:")
      if (hit.source.startsWith('artifact:')) {
        const normalizedId = normalizeArtifactId(hit.source);

        // First check if it's already in the artifacts list
        let matchingArtifact = workspaceState.artifacts.find((artifact) => {
          const normalizedArtifactId = normalizeArtifactId(artifact.id);
          return normalizedArtifactId === normalizedId || artifact.id === hit.source;
        });

        // If not found, refresh artifacts and try again
        if (!matchingArtifact) {
          await refreshArtifacts();
          matchingArtifact = workspaceState.artifacts.find((artifact) => {
            const normalizedArtifactId = normalizeArtifactId(artifact.id);
            return normalizedArtifactId === normalizedId || artifact.id === hit.source;
          });
        }

        if (matchingArtifact) {
          const id = normalizeArtifactId(matchingArtifact.id);
          openArtifact(id);
          navigate(`/?artifact=${id}`);
        } else {
          // Try opening directly with the normalized ID (might work if backend loads it)
          openArtifact(normalizedId);
          navigate(`/?artifact=${normalizedId}`);
        }
        setIsCommandOpen(false);
        setQuery('');
        return;
      }

      // For indexed documents (not artifacts), try to find existing artifact by title first
      let matchingArtifact = workspaceState.artifacts.find(
        (artifact) => artifact.title === descriptor.label || artifact.title === hit.source,
      );

      if (matchingArtifact) {
        // Found existing artifact, open it
        const id = normalizeArtifactId(matchingArtifact.id);
        openArtifact(id);
        navigate(`/?artifact=${id}`);
      } else {
        // Not found - fetch content and create a new artifact from the indexed document
        try {
          const contentResponse = await getDocumentContent(hit.source);
          const newArtifact = await createDocument(descriptor.label, contentResponse.content, 'user');
          const id = normalizeArtifactId(newArtifact.id);
          openArtifact(id);
          navigate(`/?artifact=${id}`);
          toast.success(`Opened "${descriptor.label}" in workspace`);
        } catch (error) {
          console.error('Failed to fetch document content:', error);
          toast.error(`Failed to open document "${descriptor.label}"`);
        }
      }
      setIsCommandOpen(false);
      setQuery('');
    },
    [workspaceState.artifacts, openArtifact, refreshArtifacts, createDocument, navigate],
  );

  // Filter commands based on query
  const filteredCommands = useMemo(() => {
    if (!query.trim()) {
      return commands;
    }
    return commands.filter(
      (cmd) =>
        cmd.label.toLowerCase().includes(query.toLowerCase()) ||
        cmd.description?.toLowerCase().includes(query.toLowerCase()),
    );
  }, [query, commands]);

  // Combine commands and search results
  const allItems: SearchResultItem[] = useMemo(() => {
    // If search has been performed, show search results; otherwise show filtered commands
    if (hasSearched && searchResults.length > 0) {
      const documentItems: SearchResultItem[] = searchResults.map((hit) => {
        const descriptor = describeSearchSource(hit.source, workspaceState.artifacts);
        return {
          id: hit.id,
          label: descriptor.label,
          description: hit.snippet || descriptor.detail,
          snippet: hit.snippet,
          source: hit.source,
          type: 'document' as const,
          icon: <Icon name="Knowledge" className="w-4 h-4" />,
          action: () => handleOpenDocument(hit),
        };
      });
      return documentItems;
    }

    // Before search or if no results, show filtered commands
    return filteredCommands.map((cmd) => ({
      id: cmd.id,
      label: cmd.label,
      description: cmd.description,
      icon: cmd.icon,
      type: 'command' as const,
      action: cmd.action,
    }));
  }, [filteredCommands, searchResults, handleOpenDocument, hasSearched, workspaceState.artifacts]);

  // Reset selection when filtered results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [query, allItems.length]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && allItems.length > 0) {
      const selectedEl = listRef.current.children[selectedIndex] as HTMLElement;
      selectedEl?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex, allItems.length]);

  // Global keyboard shortcut to open command palette
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsCommandOpen(true);
      }
      if (e.key === 'Escape' && isCommandOpen) {
        e.preventDefault();
        setIsCommandOpen(false);
        setQuery('');
        setSearchResults([]);
        setHasSearched(false);
        setLastSearchQuery('');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isCommandOpen]);

  // Focus input when command palette opens
  useEffect(() => {
    if (isCommandOpen) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isCommandOpen]);

  const handleItemSelect = useCallback(
    (item: SearchResultItem) => {
      item.action();
    },
    [],
  );

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, allItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // If we have search results (documents) and an item is selected, select it
      if (hasSearched && searchResults.length > 0 && allItems.length > 0 && allItems[selectedIndex]?.type === 'document') {
        handleItemSelect(allItems[selectedIndex]);
      } else if (query.trim()) {
        // Otherwise, if query exists, perform search
        handleSearch();
      }
    }
  };

  return (
    <>
      {/* Top Bar */}
      <header className="h-14 bg-white border-b border-neutral-200 flex items-center px-4 gap-4 shrink-0 z-50">
        {/* Mobile menu button */}
        <button
          type="button"
          onClick={() => setIsMobileMenuOpen(true)}
          className="lg:hidden flex items-center justify-center w-9 h-9 rounded-lg text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 transition-colors"
          aria-label="Open menu"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {/* Logo - icon only */}
        <RouterLink
          to="/"
          className="flex items-center shrink-0 hover:opacity-80 transition-opacity"
        >
          <StartupIcon size={36} variant="filled" className="h-9 w-9" />
        </RouterLink>

        {/* Search/Do Bar - centered, slim */}
        <div className="flex-1 flex justify-center px-2 sm:px-4">
          <button
            type="button"
            onClick={() => setIsCommandOpen(true)}
            className="group relative flex items-center gap-2 w-full max-w-md h-7 px-2.5 rounded border border-neutral-200 bg-neutral-50/60 hover:bg-white hover:border-neutral-300 hover:shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
          >
            {/* Search icon */}
            <svg
              className="w-3.5 h-3.5 text-neutral-400 group-hover:text-neutral-500 transition-colors shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <circle cx="11" cy="11" r="8" strokeWidth="2" />
              <path d="M21 21l-4.35-4.35" strokeWidth="2" strokeLinecap="round" />
            </svg>

            {/* Placeholder text */}
            <span className="text-[13px] text-neutral-400 group-hover:text-neutral-500 transition-colors truncate">
              <span className="hidden sm:inline">Search or do anything...</span>
              <span className="sm:hidden">Search...</span>
            </span>

            {/* Keyboard shortcut hint */}
            <div className="absolute right-2 hidden sm:flex items-center gap-0.5">
              <kbd className="inline-flex items-center justify-center h-4 px-1 text-[9px] font-medium text-neutral-400 bg-neutral-100 border border-neutral-200 rounded group-hover:bg-neutral-50 group-hover:border-neutral-300 transition-colors">
                {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}
              </kbd>
              <kbd className="inline-flex items-center justify-center h-4 px-1 text-[9px] font-medium text-neutral-400 bg-neutral-100 border border-neutral-200 rounded group-hover:bg-neutral-50 group-hover:border-neutral-300 transition-colors">
                K
              </kbd>
            </div>
          </button>
        </div>

      </header>

      {/* Mobile Navigation Drawer */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 z-[60] lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />

          {/* Drawer */}
          <div className="fixed inset-y-0 left-0 w-72 bg-white shadow-xl z-[61] lg:hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-4 h-14 border-b border-neutral-200">
              <RouterLink
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center"
              >
                <StartupIcon size={32} variant="filled" className="h-8 w-8" />
              </RouterLink>
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center justify-center w-8 h-8 rounded-lg text-neutral-500 hover:bg-neutral-100"
                aria-label="Close menu"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Navigation Links */}
            <nav className="flex-1 overflow-y-auto py-4 px-3">
              <div className="space-y-1">
                {navLinks.map(({ title, to, icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === '/'}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={({ isActive }) => [
                      'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors',
                      isActive
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900',
                    ].join(' ')}
                  >
                    <Icon name={icon} className="w-5 h-5" />
                    {title}
                  </NavLink>
                ))}
              </div>
            </nav>

            {/* Sign out button */}
            <div className="p-3 border-t border-neutral-200">
              <button
                type="button"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  handleSignOut();
                }}
                disabled={signingOut}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 transition-colors disabled:opacity-60"
              >
                <Icon name="Logout" className="w-5 h-5" />
                {signingOut ? 'Signing out…' : 'Sign out'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Command Palette Modal */}
      {isCommandOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] animate-in fade-in duration-150"
            onClick={() => {
              setIsCommandOpen(false);
              setQuery('');
              setSearchResults([]);
              setHasSearched(false);
              setLastSearchQuery('');
            }}
          />

          {/* Modal */}
          <div className="fixed left-1/2 top-16 sm:top-[20%] -translate-x-1/2 w-[calc(100%-1.5rem)] sm:w-full max-w-lg z-[101] animate-in fade-in slide-in-from-top-4 duration-200">
            <div className="bg-white rounded-2xl shadow-2xl border border-neutral-200 overflow-hidden">
              {/* Search Input */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-neutral-100">
                <svg
                  className="w-5 h-5 text-neutral-400 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <circle cx="11" cy="11" r="8" strokeWidth="2" />
                  <path d="M21 21l-4.35-4.35" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleInputKeyDown}
                  placeholder="Type a command or press Enter to search..."
                  className="flex-1 text-sm text-neutral-900 placeholder:text-neutral-400 bg-transparent border-none outline-none"
                />
                <kbd className="flex items-center justify-center h-5 px-1.5 text-[10px] font-medium text-neutral-400 bg-neutral-100 border border-neutral-200 rounded">
                  ESC
                </kbd>
              </div>

              {/* Results */}
              <div ref={listRef} className="max-h-80 overflow-y-auto p-2">
                {isSearching && query.trim() ? (
                  <div className="px-4 py-8 text-center text-sm text-neutral-500">
                    Searching...
                  </div>
                ) : allItems.length === 0 && query.trim() && hasSearched ? (
                  <div className="px-4 py-8 text-center text-sm text-neutral-500">
                    No results found for "{query}"
                  </div>
                ) : allItems.length === 0 && query.trim() && !hasSearched ? (
                  <div className="px-4 py-8 text-center text-sm text-neutral-500">
                    Press Enter to search for "{query}"
                  </div>
                ) : (
                  <>
                    {allItems.map((item, index) => {
                      const isDocument = item.type === 'document';
                      const descriptor = item.source ? describeSearchSource(item.source, workspaceState.artifacts) : null;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => handleItemSelect(item)}
                          onMouseEnter={() => setSelectedIndex(index)}
                          className={[
                            'w-full flex items-start gap-3 px-3 py-2.5 rounded-lg text-left transition-colors',
                            index === selectedIndex
                              ? 'bg-blue-50 text-blue-900'
                              : 'text-neutral-700 hover:bg-neutral-50',
                          ].join(' ')}
                        >
                          {/* Icon */}
                          <div
                            className={[
                              'flex items-center justify-center w-8 h-8 rounded-lg shrink-0 mt-0.5',
                              index === selectedIndex ? 'bg-blue-100' : 'bg-neutral-100',
                            ].join(' ')}
                          >
                            {item.icon}
                          </div>

                          {/* Label & Description */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <div className="text-sm font-medium truncate">{item.label}</div>
                              {isDocument && descriptor && (
                                <span className="inline-flex shrink-0 items-center rounded-full border border-neutral-200 bg-neutral-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-600">
                                  {descriptor.type}
                                </span>
                              )}
                            </div>
                            {item.description && (
                              <div
                                className={[
                                  'text-xs truncate mt-0.5',
                                  index === selectedIndex ? 'text-blue-700' : 'text-neutral-500',
                                ].join(' ')}
                              >
                                {item.description}
                              </div>
                            )}
                          </div>

                          {/* Shortcut - only for commands */}
                          {!isDocument && commands.find((c) => c.id === item.id)?.shortcut && (
                            <div className="flex items-center gap-1 shrink-0">
                              {commands
                                .find((c) => c.id === item.id)
                                ?.shortcut?.split(' ')
                                .map((key, i) => (
                                  <kbd
                                    key={i}
                                    className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 text-[10px] font-medium text-neutral-400 bg-neutral-100 border border-neutral-200 rounded"
                                  >
                                    {key}
                                  </kbd>
                                ))}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </>
                )}
              </div>

              {/* Footer hint */}
              <div className="px-4 py-2.5 border-t border-neutral-100 bg-neutral-50/50">
                <div className="flex items-center gap-4 text-[11px] text-neutral-400">
                  <span className="flex items-center gap-1">
                    <kbd className="inline-flex items-center justify-center h-4 px-1 text-[9px] bg-neutral-100 border border-neutral-200 rounded">
                      ↑↓
                    </kbd>
                    navigate
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="inline-flex items-center justify-center h-4 px-1 text-[9px] bg-neutral-100 border border-neutral-200 rounded">
                      ↵
                    </kbd>
                    {hasSearched ? 'select' : 'search'}
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="inline-flex items-center justify-center h-4 px-1 text-[9px] bg-neutral-100 border border-neutral-200 rounded">
                      esc
                    </kbd>
                    close
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

